import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonLink } from 'components/shared/ButtonLink';
import { useI18n } from 'hooks/useI18n';
import { useState } from 'react';
import { getPublicPath } from 'utils/paths';
import {
  StyledBadge,
  StyledCard,
  StyledInfo,
  StyledInfoItem,
  StyledMemberExperience,
  StyledMemberItem,
  StyledMembers,
  StyledShowMore,
  StyledTechItem,
  StyledTechs,
  StyledTitle
} from './BringYourOwnTeamCard.styled';

type TechProps = {
  tech: string;
  icon: string;
};

type MemberProps = {
  role: string;
  roleType: string;
  experience: string;
};

type TeamItemProps = {
  stack: TechProps[];
  salary: string;
  location: string;
  members: MemberProps[];
  typeformCode: string;
};

type TeamProps = {
  team: TeamItemProps;
};

const BringYourOwnTeamCard = ({ team }: TeamProps) => {
  const { t } = useI18n('bring-your-own-team');

  const [showMembers, setShowMembers] = useState(false);

  function showExtraMembers(): void {
    setShowMembers(true);
  }

  const roleIcons: Record<string, string> = {
    backend: getPublicPath('/images/experience/backend.png'),
    data: getPublicPath('/images/experience/data.png'),
    design: getPublicPath('/images/experience/design.png'),
    frontend: getPublicPath('/images/experience/frontend.png'),
    leadership: getPublicPath('/images/experience/leadership.png'),
    misc: getPublicPath('/images/experience/misc.png'),
    mobile: getPublicPath('/images/experience/mobile.png'),
    productmgmt: getPublicPath('/images/experience/productmgmt.png'),
    systems: getPublicPath('/images/experience/systems.png')
  };

  return (
    <StyledCard>
      <StyledBadge>
        {t('teams.cards.cardTitle')} {team.members.length}
      </StyledBadge>
      <div>
        <StyledTitle>{t('teams.cards.stackTitle')}</StyledTitle>
        <StyledTechs>
          {team.stack.map((s, i) => (
            <StyledTechItem key={i}>
              <img src={s.icon} alt="" />
              <span>{s.tech} </span>
            </StyledTechItem>
          ))}
        </StyledTechs>
      </div>
      <StyledInfo>
        <StyledInfoItem>
          <StyledTitle>{t('teams.cards.salaryTitle')}</StyledTitle>
          <span>{team.salary}</span>
        </StyledInfoItem>
        <StyledInfoItem>
          <StyledTitle>{t('teams.cards.ubicationTitle')}</StyledTitle>
          <span>{team.location}</span>
        </StyledInfoItem>
      </StyledInfo>
      <StyledMembers>
        {team.members.slice(0, 5).map((m, i) => (
          <StyledMemberItem key={i}>
            <img src={roleIcons[m.roleType] || roleIcons['misc']} alt="" />
            <div>
              <span>{m.role}</span>
              <StyledMemberExperience>{m.experience}</StyledMemberExperience>
            </div>
          </StyledMemberItem>
        ))}
      </StyledMembers>
      {team.members.length > 5 && (
        <StyledShowMore
          onClick={showExtraMembers}
          className={`${showMembers ? 'hide' : ''}`}
        >
          {t('teams.cards.showMore')}
        </StyledShowMore>
      )}
      {showMembers && (
        <StyledMembers>
          {team.members.slice(5, team.members.length).map((m, i) => (
            <StyledMemberItem key={i}>
              <img
                src={getPublicPath('/images/experience/' + m.roleType + '.png')}
                alt=""
              />
              <div>
                <span>{m.role}</span>
                <StyledMemberExperience>{m.experience}</StyledMemberExperience>
              </div>
            </StyledMemberItem>
          ))}
        </StyledMembers>
      )}
      <ButtonLink
        variant="primary"
        href={
          'https://getmanfred.typeform.com/to/eTkbS7x5/' + team.typeformCode
        }
      >
        <FontAwesomeIcon icon={faCheckCircle} />
        <span>{t('teams.cards.CTA')}</span>
      </ButtonLink>
    </StyledCard>
  );
};

export default BringYourOwnTeamCard;
